import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

// Edit an existing lesson package. Opens pre-filled from the package row; the
// server re-checks ownership on save.
export const PackageEditDialog = ({ pkg, onOpenChange, onSave }) => {
  const [form, setForm] = useState(pkg);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(pkg); }, [pkg]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const price = Number(form?.price) || 0;
  const discount = Number(form?.discount_percent) || 0;
  const invalid = !form?.title?.trim() || !(Number(form?.hours) >= 1) || !(price > 0);

  const save = async () => {
    if (invalid) return;
    setBusy(true);
    try {
      await onSave({
        title: form.title.trim(),
        description: form.description || "",
        hours: Number(form.hours),
        price: price,
        discount_percent: discount,
      });
    } finally {
      setBusy(false);
    }
  };

  if (!form) return null;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="pkg-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit package</DialogTitle>
          <DialogDescription>
            Changes apply to new purchases only — learners who already booked keep the price they paid.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-3">
            <Label>Title</Label>
            <Input value={form.title || ""} onChange={set("title")} className="rounded-sm" data-testid="pkg-edit-title" />
          </div>
          <div>
            <Label>Lessons (hours)</Label>
            <Input type="number" min={1} max={100} value={form.hours ?? ""} onChange={set("hours")} className="rounded-sm" data-testid="pkg-edit-hours" />
          </div>
          <div>
            <Label>Price (CAD)</Label>
            <Input type="number" min={1} value={form.price ?? ""} onChange={set("price")} className="rounded-sm" data-testid="pkg-edit-price" />
          </div>
          <div>
            <Label>Discount %</Label>
            <Input type="number" min={0} max={90} value={form.discount_percent ?? 0} onChange={set("discount_percent")} className="rounded-sm" data-testid="pkg-edit-discount" />
          </div>
          <div className="sm:col-span-3">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description || ""} onChange={set("description")} className="rounded-sm resize-none" data-testid="pkg-edit-desc" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-muted-foreground" data-testid="pkg-edit-preview">
            Learners pay <span className="font-medium text-foreground">${(price * (1 - discount / 100)).toFixed(2)}</span>
            {discount > 0 && <span className="line-through ml-2">${price.toFixed(2)}</span>}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="pkg-edit-cancel">Cancel</Button>
            <Button className="rounded-sm" disabled={invalid || busy} onClick={save} data-testid="pkg-edit-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackageEditDialog;
